import type {
  BuildingGeometryEmitter,
  BuildingProp,
  MaterialSlotRemapping,
} from "building-types/geometry/buildingGeometryTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import type { FaceCategory } from "building-types/materials/faceCategories";
import type { InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import type { InteriorMaterialOverride, ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { PropAttributeTable, Prop } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import type { ViewEntityType } from "building-types/views/viewEntityType";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialSrcFromPalette } from "doc/materialLookup";
import { faceGroupNew } from "geom/brep";
import { geomToFaces } from "geom/geom";
import type GeomTagGroup from "geom/geomTagGroup";
import { BUILDING_INTERIOR_LAYER, CLIPPING_LAYER, BUILDING_EXTERIOR_LAYER, UI_LAYER } from "gfx/layerConstants";
import { mergePropMaterialOverrides, resolveUseExteriorPalette } from "../materials/propMaterialMappingCore";

export function buildInteriorLayers(clip: boolean = true) {
  const layers = [BUILDING_INTERIOR_LAYER];
  if (clip) {
    layers.push(CLIPPING_LAYER);
  }

  return layers;
}

export function buildExteriorLayers(clip: boolean = true) {
  const layers = [BUILDING_EXTERIOR_LAYER];
  if (clip) {
    layers.push(CLIPPING_LAYER);
  }

  return layers;
}

export function buildUILayers(clip: boolean = true) {
  const layers = [UI_LAYER];
  if (clip) {
    layers.push(CLIPPING_LAYER);
  }

  return layers;
}

export interface GetInteriorGroupsGeometriesOptions {
  readonly levelId: string | undefined;
  readonly groups: GeomTagGroup<InteriorMaterialOverride>[];
  readonly palette: InteriorPalette;
  readonly room: Room | undefined;
  readonly faceCategory: FaceCategory;
  readonly clip?: boolean;
  readonly materialOverrideTable?: { [key: string]: MaterialSrc };
  readonly pickHandle?: DocEntityHandle;
  readonly entityId?: string;
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
  readonly emitter: BuildingGeometryEmitter;
}

/**
 * Generate BuildingGeometry for interior groups.
 * Processes each geometry individually to preserve per-geometry viewEntityDescriptor.
 */
export function getInteriorGroupsGeometries(options: GetInteriorGroupsGeometriesOptions): void {
  const {
    levelId,
    groups,
    palette,
    room,
    faceCategory,
    clip = true,
    materialOverrideTable,
    pickHandle,
    entityId,
    entityHandleForSelectingReportingUnit,
    emitter,
  } = options;

  if (!room || !groups) {
    return;
  }

  const threeLayers = buildInteriorLayers(clip);

  for (const { geom, tags } of groups) {
    for (let i = 0, l = geom.length; i < l; i++) {
      const { interiorMaterial, uvGen, viewEntityDescriptor, exportComponentName } = tags[i];
      const geo = geom[i];

      // Resolve material - always include geometry even if material is undefined
      // (matches old behavior where faces are categorized regardless of material resolution)
      let materialSrc: MaterialSrc | undefined;
      if (materialOverrideTable && materialOverrideTable[interiorMaterial]) {
        materialSrc = materialOverrideTable[interiorMaterial];
      } else {
        materialSrc = materialSrcFromPalette(palette, interiorMaterial, room?.type);
      }

      const faces = geomToFaces(geo);

      emitter.emit({
        geom: faceGroupNew(faces),
        faceCategory,
        threeLayers,
        levelId,
        pickHandle,
        entityId: entityId ?? viewEntityDescriptor?.entityId,
        entityHandleForSelectingReportingUnit,
        descriptor: viewEntityDescriptor,
        exportComponentName,
        materialSlot: interiorMaterial,
        materialSrc,
        uvGen,
      });
    }
  }
}

export interface GetExteriorGroupsGeometriesOptions {
  readonly levelId: string | undefined;
  readonly groups: GeomTagGroup<ExteriorMaterialOverride>[];
  readonly palette: ExteriorPalette;
  readonly faceCategory: FaceCategory;
  readonly setToExteriorLayer?: boolean;
  readonly clip?: boolean;
  readonly pickHandle?: DocEntityHandle;
  readonly entityId?: string;
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
  readonly viewEntityType?: ViewEntityType;
  readonly emitter: BuildingGeometryEmitter;
}

/**
 * Generate BuildingGeometry for exterior groups.
 * Processes each geometry individually to preserve per-geometry viewEntityDescriptor.
 */
export function getExteriorGroupsGeometries(options: GetExteriorGroupsGeometriesOptions): void {
  const {
    levelId,
    groups,
    palette,
    faceCategory,
    setToExteriorLayer = true,
    clip,
    pickHandle,
    entityId,
    entityHandleForSelectingReportingUnit,
    viewEntityType,
    emitter,
  } = options;

  if (!groups) {
    return;
  }

  const threeLayers = setToExteriorLayer ? buildExteriorLayers(clip) : [];

  for (const { geom, tags } of groups) {
    for (let i = 0, l = geom.length; i < l; i++) {
      const { exteriorMaterial, uvGen, viewEntityDescriptor, materialTransformation, exportComponentName } = tags[i];
      const geo = geom[i];

      // Always include geometry even if material is undefined - matches old behavior
      // where faces are categorized regardless of material resolution
      const materialSrc = exteriorMaterial ? materialSrcFromPalette(palette, exteriorMaterial) : undefined;

      const faces = geomToFaces(geo);

      emitter.emit({
        geom: faceGroupNew(faces),
        faceCategory,
        threeLayers,
        levelId,
        pickHandle,
        entityId: entityId ?? viewEntityDescriptor?.entityId,
        entityHandleForSelectingReportingUnit,
        descriptor: viewEntityDescriptor,
        exportComponentName,
        viewEntityType, // Used to create entity IDs when viewEntityDescriptor is not available
        materialSlot: exteriorMaterial,
        materialSrc,
        uvGen,
        materialTransformation,
      });
    }
  }
}

/**
 * Minimal prop interface for createBuildingProp helper.
 * Includes only the fields needed for BuildingProp construction.
 */
export interface PropLike
  extends Pick<
    Prop,
    | "src"
    | "x"
    | "y"
    | "z"
    | "rotationDegrees"
    | "scaleX"
    | "scaleY"
    | "scaleZ"
    | "materialOverrides"
    | "useExteriorPalette"
    | "isExterior"
  > {}

/**
 * Options for createBuildingProp helper - context-dependent and computed fields.
 */
export interface CreateBuildingPropOptions {
  readonly threeLayers: number[];
  readonly faceCategory: FaceCategory;
  readonly propCatalog: PropAttributeTable;
  readonly levelId?: string;
  readonly aboveLevel?: boolean;
  readonly pickHandle?: DocEntityHandle;
  readonly entityId?: string;
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
  readonly materialRemapping?: MaterialSlotRemapping;
  readonly roomType?: RoomType;
}

/**
 * Create a BuildingProp from a prop-like object and context options.
 * Centralizes prop-to-BuildingProp transformation, handling material override merging
 * and useExteriorPalette resolution.
 *
 * @param prop - The source prop with transform and material data
 * @param options - Context-dependent fields (layers, catalog, etc.)
 * @returns BuildingProp ready for emission, or undefined if assetId cannot be resolved
 */
export function createBuildingProp(prop: PropLike, options: CreateBuildingPropOptions): BuildingProp | undefined {
  const {
    threeLayers,
    faceCategory,
    propCatalog,
    levelId,
    aboveLevel,
    pickHandle,
    entityId,
    entityHandleForSelectingReportingUnit,
    materialRemapping,
    roomType,
  } = options;

  // Resolve asset ID from prop catalog
  const propAttributes = propCatalog[prop.src];
  const assetId = propAttributes?.propAssetId;
  if (assetId === undefined) {
    return undefined;
  }

  return {
    threeLayers,
    pickHandle,
    entityId,
    entityHandleForSelectingReportingUnit,
    levelId,
    aboveLevel,
    faceCategory,
    assetId,
    src: prop.src,
    position: [prop.x, prop.y, prop.z],
    rotationDegrees: prop.rotationDegrees,
    scale: [prop.scaleX, prop.scaleY, prop.scaleZ],
    mergedMaterialOverrides: mergePropMaterialOverrides(prop.src, prop.materialOverrides, propCatalog),
    useExteriorPalette: resolveUseExteriorPalette(prop.src, prop.isExterior, prop.useExteriorPalette, propCatalog),
    roomType,
    materialRemapping,
  };
}

/**
 * Create and emit a BuildingProp in one step.
 * Combines createBuildingProp with the emitter call, handling the undefined check internally.
 *
 * @param emitter - The geometry emitter (must have emitProp defined)
 * @param prop - The source prop with transform and material data
 * @param options - Context-dependent fields (layers, catalog, etc.)
 * @returns true if prop was emitted, false if asset not found
 */
export function emitBuildingProp(
  emitter: BuildingGeometryEmitter,
  prop: PropLike,
  options: CreateBuildingPropOptions,
): boolean {
  if (!emitter.emitProp) {
    return false;
  }
  const buildingProp = createBuildingProp(prop, options);
  if (buildingProp) {
    emitter.emitProp(buildingProp);
    return true;
  }
  return false;
}
